import { useHoneyGridApiFetch } from '@/queries/useHoneyGridApiFetch'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { WorkspaceId } from 'honeygrid-types'
import { R2UploadedPartValidator } from 'honeygrid-types/R2Multipart'

import { dataSourcesQueryKey } from './useDataSources'

interface UploadResult {
	success: boolean
	dataSourceId: string
}

interface Params {
	workspaceId: WorkspaceId
	file: File
	name: string
}

// 5 MiB
const PART_SIZE = 5 * 1024 * 1024

export const useUploadDataSource = () => {
	const queryClient = useQueryClient()
	const authFetch = useHoneyGridApiFetch()

	return useMutation<UploadResult, unknown, Params>({
		mutationFn: async ({ workspaceId, file, name }) => {
			// Step 1: init multipart upload
			const initRes = (await authFetch(`workspaces/${workspaceId}/data-sources/upload/init`, {
				method: 'POST',
				body: { name },
			})) as {
				dataSourceId: string
				uploadId: string
				key: string
			}

			const { dataSourceId, uploadId } = initRes

			// Step 2: upload parts (5 MiB each)
			const totalParts = Math.ceil(file.size / PART_SIZE)
			const parts: { partNumber: number; etag: string }[] = []

			for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
				const start = (partNumber - 1) * PART_SIZE
				const end = Math.min(start + PART_SIZE, file.size)
				const blobPart = file.slice(start, end)
				const arrayBuffer = await blobPart.arrayBuffer()

				const rawRes = await authFetch(
					`workspaces/${workspaceId}/data-sources/${dataSourceId}/upload?uploadId=${uploadId}&partNumber=${partNumber}`,
					{
						method: 'PUT',
						body: arrayBuffer,
						headers: { 'Content-Type': 'application/octet-stream' },
					}
				)
				const partRes = R2UploadedPartValidator.parse(rawRes)
				parts.push(partRes)
			}

			// Step 3: complete multipart upload
			await authFetch(`workspaces/${workspaceId}/data-sources/${dataSourceId}/upload/complete`, {
				method: 'POST',
				body: { uploadId, parts },
			})

			return { success: true, dataSourceId }
		},
		onSuccess: (_data, variables) => {
			// invalidate list for workspace
			queryClient.invalidateQueries({
				queryKey: dataSourcesQueryKey(variables.workspaceId),
			})
		},
	})
}
